import React, { useEffect } from 'react';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  useEffect(() => {
    // Load Calendly widget script dynamically
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  return (
    <div className="privacy-page">
      {/* Sub Hero Banner */}
      <section
        className="privacy-hero"
        style={{
          backgroundImage: ` url('${process.env.PUBLIC_URL}/assets/images/home-banner.jpg')`
        }}
      >
        <div className="privacy-hero__container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <h1 className="privacy-hero__title" style={{ marginBottom: '8px' }}>Privacy Policy</h1>
          <nav className="about-hero__breadcrumb" aria-label="breadcrumb">
            <a href="/" className="about-hero__breadcrumb-link">
              Home
            </a>
            <span className="about-hero__breadcrumb-separator">&gt;</span>
            <span className="about-hero__breadcrumb-current">Privacy Policy</span>
          </nav>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="privacy-content-section">
        <div className="privacy-content__container">
          <p className="privacy-intro">
            Welcome to <a href="https://alquranlesson.com" className="privacy-link">alquranlesson.com</a>. We are committed to protecting your privacy and ensuring the security of your personal information. This privacy policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services.
            <br />
            By using our Website, you agree to the terms of this Privacy Policy. If you do not agree with the terms, please do not access or use the Website.
          </p>

          <h2 className="privacy-sec-title">1. Information We Collect</h2>
          <p className="privacy-text">We may collect the following types of information:</p>

          <p className="privacy-sub-title">Personal Information:</p>
          <p className="privacy-text">When you fill out forms on our Website, we may collect personal information such as:</p>
          <ul className="privacy-list">
            <li>Your name</li>
            <li>Email address</li>
            <li>Phone number (including to phone numbers for SMS text communication)</li>
            <li>Payment information (if applicable)</li>
            <li>Any other information you voluntarily provide.</li>
          </ul>

          <p className="privacy-sub-title">Non-Personal Information:</p>
          <p className="privacy-text">We may automatically collect non-personal information such as:</p>
          <ul className="privacy-list">
            <li>IP address</li>
            <li>Browser type</li>
            <li>Operating system</li>
            <li>Pages visited</li>
            <li>Time and date of visits</li>
            <li>Referring website addresses.</li>
          </ul>

          <h2 className="privacy-sec-title">2. How We Use Your Information</h2>
          <p className="privacy-text">We use the information we collect for the following purposes:</p>
          <ul className="privacy-list">
            <li>To provide, operate, and maintain our services.</li>
            <li>To communicate with you via SMS, phone calls, or email regarding your account, lessons, or inquiries.</li>
            <li>To process payments (if applicable).</li>
            <li>To improve our Website and services.</li>
            <li>To send promotional materials, updates, or newsletters (you may opt-out at any time).</li>
            <li>To comply with legal obligations and protect our rights.</li>
          </ul>

          <h2 className="privacy-sec-title">3. SMS and Call Communication</h2>
          <p className="privacy-text">By providing your phone number, you expressly consent to receive SMS (text messages) and phone calls from us for the following purposes:</p>
          <ul className="privacy-list">
            <li>Confirming your registration or inquiries.</li>
            <li>Providing updates about your lessons, account, or schedule.</li>
            <li>Sending important service-related notifications (e.g., system maintenance, rescheduling).</li>
            <li>Sending promotional offers, updates, or newsletters (you may opt-out at any time).</li>
          </ul>

          <p className="privacy-sub-sub-title">Frequency of Messages:</p>
          <p className="privacy-text">The number of SMS messages you receive will vary based on your interaction with our services. Typically, you may receive 1-3 messages per week, depending on your lesson schedule and preferences.</p>

          <p className="privacy-sub-sub-title">Sign-Out Instructions:</p>
          <p className="privacy-text">You can opt-out of receiving SMS messages at any time by replying "STOP" to any message. Upon receiving your opt-out request, we will stop sending SMS messages to your number. For assistance, you can contact us at info@alquranlesson.com.</p>

          <p className="privacy-sub-sub-title">Message and Data Rates:</p>
          <p className="privacy-text">Standard messaging and data rates may apply, depending on your mobile carrier. Alquranlesson.com is not responsible for any charges incurred as a result of SMS communication.</p>

          <p className="privacy-sub-sub-title">Support:</p>
          <p className="privacy-text">For help or questions regarding SMS communication, reply "HELP" to any message or contact us at <a href="mailto:info@alquranlesson.com" className="privacy-link">info@alquranlesson.com</a>.</p>

          <h2 className="privacy-sec-title">4. Sharing Your Information</h2>
          <p className="privacy-text">We do not sell, trade, or rent your personal information to third parties. However, we may share your information in the following circumstances:</p>
          <ul className="privacy-list">
            <li>To comply with legal requirements, such as a subpoena or court order.</li>
            <li>To protect our rights, property, or safety, or that of others.</li>
          </ul>

          <h2 className="privacy-sec-title">5. Data Security</h2>
          <p className="privacy-text">No mobile information will be shared with third parties/affiliates for marketing/promotional purposes. All the other categories exclude text messaging originator opt-in data and consent; this information will not be shared with any third parties.</p>

          <h2 className="privacy-sec-title">6. Your Rights</h2>
          <p className="privacy-text">You have the right to:</p>
          <ul className="privacy-list">
            <li>Access, update, or delete your personal information.</li>
            <li>Opt-out of receiving promotional communications.</li>
            <li>Withdraw consent for SMS/text communication.</li>
            <li>To exercise these rights, please contact us at <a href="mailto:info@alquranlesson.com" className="privacy-link">info@alquranlesson.com</a>.</li>
          </ul>

          <h2 className="privacy-sec-title">7. Changes to This Privacy Policy</h2>
          <p className="privacy-text">We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated effective date. Your continued use of the Website after changes constitutes your acceptance of the revised policy.</p>

          <h3 className="privacy-sec-title">8. Contact Us</h3>
          <p className="privacy-text">If you have any questions about this Privacy Policy, please contact us at:</p>
          <p className="privacy-text" style={{ marginBottom: '0' }}>
            <a href="mailto:info@alquranlesson.com" className="privacy-link">info@alquranlesson.com</a> | <a href="tel:+17164577657" className="privacy-link">+1 (716) 457-7657</a>
          </p>
        </div>
      </section>

      {/* Free Consultation Call & Calendly Widget Section */}
      <section
        className="consultation-section"
        id="consultation"
        style={{
          '--bg-url': `url('${process.env.PUBLIC_URL}/assets/images/online-quran-classes-texas/edb9b1a643ea3ab6044ffcbbb4fe0032.png')`
        }}
      >
        <div className="consultation-section__container">
          <div className="consultation-section__header">
            <h2 className="consultation-section__title">
              FREE CONSULTATION CALL – Ask From Experts
            </h2>
            <div className="consultation-section__subtitle">
              <ul style={{ listStyleType: 'disc', display: 'inline-block', textAlign: 'left', paddingLeft: '20px', margin: '0 auto' }}>
                <li>Get answers to your questions – Find about our Holy Quran learning services</li>
                <li>Recommendations & suggestions from experts</li>
              </ul>
            </div>
          </div>

          <div className="consultation-section__grid">
            <div className="consultation-section__widget-container">
              <div
                className="calendly-inline-widget"
                data-url="https://calendly.com/alquranlesson/call-consultation"
                style={{ minWidth: '320px', height: '700px' }}
              />
            </div>

            <div className="consultation-section__info">
              <h3 className="consultation-section__info-title">CONTACT INFO</h3>
              <ul className="consultation-section__list">
                <li className="consultation-section__item">
                  <div className="consultation-section__icon-wrapper">
                    <svg className="consultation-section__icon" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <a href="https://maps.google.com/?q=Mississauga,+Ontario,+L5K,+1W2" target="_blank" rel="noopener noreferrer" className="consultation-section__link">
                    Mississauga, Ontario, L5K, 1W2
                  </a>
                </li>
                <li className="consultation-section__item">
                  <div className="consultation-section__icon-wrapper">
                    <svg className="consultation-section__icon" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.94.725l.548 2.2a1 1 0 01-.321.988l-1.305.98a10.582 10.582 0 004.872 4.872l.98-1.305a1 1 0 01.988-.321l2.2.548a1 1 0 01.725.94V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <a href="tel:+17164577657" className="consultation-section__link">+1 716 457 7657 (USA)</a>
                </li>
                <li className="consultation-section__item">
                  <div className="consultation-section__icon-wrapper">
                    <svg className="consultation-section__icon" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.94.725l.548 2.2a1 1 0 01-.321.988l-1.305.98a10.582 10.582 0 004.872 4.872l.98-1.305a1 1 0 01.988-.321l2.2.548a1 1 0 01.725.94V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <a href="tel:+16477385707" className="consultation-section__link">+1 647 738 5707 (Canada)</a>
                </li>
                <li className="consultation-section__item">
                  <div className="consultation-section__icon-wrapper">
                    <svg className="consultation-section__icon" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.94.725l.548 2.2a1 1 0 01-.321.988l-1.305.98a10.582 10.582 0 004.872 4.872l.98-1.305a1 1 0 01.988-.321l2.2.548a1 1 0 01.725.94V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <a href="tel:+447441394281" className="consultation-section__link">+44 7441 394281 (UK)</a>
                </li>
                <li className="consultation-section__item">
                  <div className="consultation-section__icon-wrapper">
                    <svg className="consultation-section__icon" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <a href="mailto:info@alquranlesson.com" className="consultation-section__link">info@alquranlesson.com</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;

